"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CircleCheck, FlaskConical, User, Settings2 } from "lucide-react";
export default function SynthesisRequestForm() {
  return <section data-section-id="223198" id="partner-with-us" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-secondary/10 blur-3xl opacity-40" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-3xl opacity-40" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-primary mb-4 tracking-tight">
            Partner With Our Mission
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-sans text-lg leading-relaxed">
            Join The Trust Foundation in restoring stability and dignity. Whether you are a community organization or a local business, let's build pathways to success together.
          </p>
        </div>

        <FormProvider formKey="partner_form_programs" formId="partner_form_programs" sectionName="Partner With Us Form" className="bg-card border border-border shadow-xl shadow-primary/5 rounded-3xl p-6 md:p-10" defaultValues={{
        name: "",
        email: "",
        organization: "",
        peptide_sequence: "",
        quantity_mg: "",
        purity: "Community Partner",
        additional_details: ""
      }} validationRules={{
        name: {
          required: "Name is required"
        },
        email: {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        organization: {
          required: "Organization name is required"
        },
        peptide_sequence: {
          required: "Please describe your interest"
        },
        quantity_mg: {
          required: "Please estimate your capacity",
          min: {
            value: 1,
            message: "Minimum value is 1"
          }
        },
        purity: {
          required: "Partnership type is required"
        },
        additional_details: {}
      }} submitText="Send Partnership Inquiry" successMessage="Thank you for reaching out. A member of our leadership team will contact you to discuss how we can transform our community together.">
          <InnerSynthesisForm />
        </FormProvider>
      </div>
    </section>;
}
function InnerSynthesisForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-secondary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-10 text-secondary-foreground" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-md font-sans">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-full border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/5 p-2 rounded-lg">
            <User className="size-5 text-primary" />
          </div>
          <h3 className="text-xl font-default font-semibold text-foreground">01. Contact Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans font-medium text-foreground/80">Full Name <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="John Smith" className="bg-background border-input focus:ring-secondary rounded-xl" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans font-medium text-foreground/80">Work Email <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="email" placeholder="jsmith@organization.org" className="bg-background border-input focus:ring-secondary rounded-xl" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="organization" render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="font-sans font-medium text-foreground/80">Organization or Business Name <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="Community Partners Inc." className="bg-background border-input focus:ring-secondary rounded-xl" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/5 p-2 rounded-lg">
            <FlaskConical className="size-5 text-primary" />
          </div>
          <h3 className="text-xl font-default font-semibold text-foreground">02. Partnership Vision</h3>
        </div>

        <FormField control={control} name="peptide_sequence" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium text-foreground/80">How would you like to collaborate? <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Textarea placeholder="Describe your organization's mission and how you envision working with The Trust Foundation..." className="min-h-[120px] font-sans text-base bg-background border-input focus:ring-secondary rounded-xl resize-y" {...field} />
              </FormControl>
              <FormDescription className="font-sans text-xs text-muted-foreground">
                Tell us about your goals for community impact and any specific programs you are interested in supporting.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/5 p-2 rounded-lg">
            <Settings2 className="size-5 text-primary" />
          </div>
          <h3 className="text-xl font-default font-semibold text-foreground">03. Engagement Details</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="quantity_mg" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans font-medium text-foreground/80">Estimated Volunteer/Staff Count <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="number" step="1" min="1" placeholder="5" className="bg-background border-input focus:ring-secondary rounded-xl" {...field} />
                </FormControl>
                <FormDescription className="font-sans text-xs text-muted-foreground">
                  Approximate number of individuals involved in the partnership.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="purity" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-sans font-medium text-foreground/80">Partnership Type <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background border-input focus:ring-secondary rounded-xl">
                      <SelectValue placeholder="Select partnership type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="Community Partner">Community Partner</SelectItem>
                    <SelectItem value="Corporate Sponsor">Corporate Sponsor</SelectItem>
                    <SelectItem value="Founders Circle">Founders Circle Member</SelectItem>
                    <SelectItem value="Service Provider">Service Provider</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-medium text-foreground/80">Additional Comments or Questions</FormLabel>
              <FormControl>
                <Textarea placeholder="Any specific timelines, resource requirements, or questions for our team..." className="min-h-[100px] bg-background border-input focus:ring-secondary rounded-xl resize-y" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3">
          <div className="text-destructive mt-0.5"></div>
          <p className="text-destructive text-sm font-sans">{submitError}</p>
        </div>}

      <div className="pt-6 border-t border-border">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto md:min-w-[260px] font-sans font-semibold tracking-wide rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Inquiry...
            </> : 'Submit Partnership Inquiry'}
        </Button>
      </div>
    </Form>;
}